import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { CONTRACT_SINGLE_FIELD_API } from '../../../constants/api'
import { EMPTY_OBJECT } from '../../../constants/common'
import { INHABITANT_STATUS } from '../../../constants/inhabitant'
import {
  SUBMENU_DEFAULT_VALUE,
  SUBMENU_PARAM_KEY,
} from '../../../constants/route'
import { ScreenContainerRef } from '../../../context'
import {
  useLinkLikeClick,
  useQueryParamSubTab,
  useRegisterReload,
  useReloadCountersAfterRefetch,
} from '../../../hooks'
import { getVisibleFilters } from '../../../utils/filter'
import { getSubSearchParam, isSubArchive } from '../../../utils/route'
import Button from '../../Button'
import { FILTER_BAR_FILTER_TRIGGER_VARIANT, FilterBar } from '../../Filter'
import { FilterDivider } from '../../FilterDivider'
import { PageActions, PageLoader } from '../../Page'
import { getEmptyStateActions, PageEmptyState } from '../../PageEmptyState'
import { Pagination } from '../../Pagination'
import { SelectionToolbar } from '../../SelectionToolbar'
import {
  getSelectedSize,
  getTableResetSortingButton,
  Table,
  TableConfigModal,
  TableContext,
  TableControlBar,
  TableControlButtons,
  TableMassAction,
} from '../../Table'
import { TablePagination } from '../../TablePagination'
import { getEntityTabFilterBarApiUrl } from '../EntityTab.utils'
import { EntityTabContractTableRow } from '../EntityTabContractTableRow'
import { EntityTabSubTabs, getEntityTabSubTabItems } from '../EntityTabSubTabs'

import {
  CONTRACT_EXCLUDE_FILTERS,
  ENTITY_TAB_CONTRACTS_DEFAULT_QA_NAME,
  ENTITY_TAB_CONTRACTS_SUB_TAB_ORDER,
  ENTITY_TAB_CONTRACTS_SUB_TAB_TEXT_KEY,
} from './EntityTabContracts.constants'
import {
  useEntityTabContractsMassActions,
  useEntityTabContractsTable,
} from './EntityTabContracts.hooks'
import {
  getContractsItemActions,
  getContractsMassActions,
  getContractsPermanentFilters,
  getContractsRowClickProps,
  getContractsSubTabQaName,
  isContractsArchiveAction,
  isContractsEditAction,
  parseContractsPermanentFilters,
} from './EntityTabContracts.utils'

import * as styles from './EntityTabContracts.module.css'

import ContractIcon from '../../../icons/contract.svg'
import PlusIcon from '../../../icons/plus.svg'

function EntityTabContracts({
  archiveAllApi,
  canViewArchive,
  counters = EMPTY_OBJECT,
  entity,
  getRowLink,
  qaName = ENTITY_TAB_CONTRACTS_DEFAULT_QA_NAME,
  scopeFilterKey,
  scopeId,
  setModal,
  onCloseModal,
  onCreate,
  onEdit,
  onEntityReload,
  onRegisterReload,
  onReloadCounters,
}) {
  const { t } = useTranslation()

  const {
    handleClick: handleLinkLikeClick,
    handleMouseDown: handleLinkLikeMouseDown,
  } = useLinkLikeClick()

  const pageScrollWrapperRef = useRef(null)

  const subTabOrder = canViewArchive
    ? ENTITY_TAB_CONTRACTS_SUB_TAB_ORDER
    : [SUBMENU_DEFAULT_VALUE]

  const { activeSubTab, setActiveSubTab } = useQueryParamSubTab({
    allowedValues: subTabOrder,
    paramKey: SUBMENU_PARAM_KEY,
    readValue: getSubSearchParam,
  })

  const filterBarApiUrl = getEntityTabFilterBarApiUrl({
    baseApi: CONTRACT_SINGLE_FIELD_API,
    scopeFilterKey,
    scopeId,
  })

  const {
    availableFilters,
    checkIsItemSelected,
    columns,
    exclude,
    excludeIds,
    filterValues,
    handleApplyConfig,
    handleCleanSelection,
    handleConfigOpen,
    handleExclude,
    handleLoadInit,
    handleRefresh,
    handleReload,
    handleResetOrdering,
    handleSelect,
    handleSelectAll,
    handleSelectAllPages,
    handleSetColumns,
    handleSetFilterValues,
    handleSetOrdering,
    handleSetPagination,
    handleSetSize,
    handleUnselectAllPages,
    hasFilterChanged,
    isAllSelected,
    isConfigOpen,
    isInitialLoadSettled,
    isLoading,
    isRefetching,
    isRemoveSelection,
    items,
    ordering,
    pagination,
    permissions,
    requiredColumns,
    selectAllPages,
    selected,
    selectedIds,
    setConfigOpen,
  } = useEntityTabContractsTable({
    containerRef: pageScrollWrapperRef,
    entity,
    objectId: scopeId,
    scopeFilterKey,
    sub: activeSubTab,
  })

  const { handleArchiveItem, isPerformingMassAction, setMassAction } =
    useEntityTabContractsMassActions({
      archiveAllApi,
      excludeIds,
      filterValues,
      pagination,
      scopeFilterKey,
      scopeId,
      selectAllPages,
      selectedIds,
      sub: activeSubTab,
      onCleanSelection: handleCleanSelection,
      onCloseModal,
      onEntityReload,
      onReload: handleReload,
      onSetModal: setModal,
    })

  const { handleReloadWithoutCounters } = useReloadCountersAfterRefetch({
    isRefetching,
    reload: handleReload,
    onReloadCounters,
  })

  useEffect(() => {
    if (scopeId) {
      handleLoadInit()
    }
  }, [activeSubTab, scopeId])

  // Expose the table reload to the profile so out-of-tab openers (e.g. the
  // header-footer "Create contract" action) can refresh a visible list.
  useRegisterReload({
    isReady: isInitialLoadSettled,
    reload: handleReloadWithoutCounters,
    onRegisterReload,
  })

  const isArchiveSubTab = isSubArchive(activeSubTab)

  const hasContracts = items?.items?.length > 0

  const showEmptyState = !hasContracts && !isLoading

  const subTabItems = getEntityTabSubTabItems({
    counters,
    subTabOrder,
    t,
    textKeys: ENTITY_TAB_CONTRACTS_SUB_TAB_TEXT_KEY,
  })

  const canCreate = permissions.can_create

  const shouldShowCreateButton = canCreate && !isArchiveSubTab

  const hasVisibleFilters = getVisibleFilters(availableFilters).length > 0

  const shouldCollapseToolbar =
    !isLoading &&
    ((!hasContracts && !hasFilterChanged) ||
      (!hasVisibleFilters && !shouldShowCreateButton))

  const massActions = getContractsMassActions({
    isArchiveSubTab,
    permissions,
    t,
  })

  function handleAddContract() {
    onCreate?.({ onReloadList: handleReload })
  }

  function getRowActions(item) {
    return getContractsItemActions(item, isArchiveSubTab)
  }

  function handleRowClick(item, event) {
    const link = getRowLink?.(item)

    if (link) {
      handleLinkLikeClick({ event, link })
    }
  }

  function handleRowMouseDown(item, event) {
    const link = getRowLink?.(item)

    if (link) {
      handleLinkLikeMouseDown({ event, link })
    }
  }

  function handleCallRowAction(action, item) {
    if (isContractsEditAction(action)) {
      onEdit?.({ item, onReloadList: handleReload })
    }

    if (isContractsArchiveAction(action)) {
      handleArchiveItem(item)
    }
  }

  const emptyStateActions = getEmptyStateActions({
    actionName: qaName.addNew,
    isLoading,
    qaName: qaName.addNew,
    shouldShowActions: shouldShowCreateButton && !hasFilterChanged,
    text: t('ContractTable.AddNewBtnKey'),
    onClick: handleAddContract,
  })

  return (
    <ScreenContainerRef value={pageScrollWrapperRef}>
      <div className={styles.EntityTabContracts} ref={pageScrollWrapperRef}>
        {isPerformingMassAction || isRefetching ? <PageLoader /> : null}
        <EntityTabSubTabs
          activeValue={activeSubTab}
          getItemQaName={getContractsSubTabQaName}
          items={subTabItems}
          onChange={setActiveSubTab}
        />
        {shouldCollapseToolbar ? null : (
          <div className={styles.toolbar}>
            {hasVisibleFilters || isLoading ? (
              <FilterBar
                responsive
                apiUrl={filterBarApiUrl}
                availableFilters={availableFilters}
                className={styles.filterBar}
                entity={entity}
                excludeFilters={CONTRACT_EXCLUDE_FILTERS}
                filterTriggerVariant={
                  FILTER_BAR_FILTER_TRIGGER_VARIANT.COMPACT_ICON
                }
                filterValues={filterValues}
                getPermanentFilters={getContractsPermanentFilters}
                isSkeleton={isLoading}
                objectId={scopeId}
                ordering={ordering}
                pagination={{ per_page: pagination?.pageSize }}
                parsePermanentFilters={parseContractsPermanentFilters}
                setFilterValues={handleSetFilterValues}
                sub={activeSubTab}
                onReload={handleReload}
              />
            ) : null}
            <PageActions className={styles.actions} isLoading={isLoading}>
              {shouldShowCreateButton ? (
                <Button.Text
                  beforeIconProps={{ Icon: PlusIcon }}
                  isSkeleton={isLoading}
                  qaName={qaName.addNew}
                  text={t('ContractTable.AddNewBtnKey')}
                  onClick={handleAddContract}
                />
              ) : null}
            </PageActions>
          </div>
        )}
        {showEmptyState ? (
          <PageEmptyState
            EntityIcon={ContractIcon}
            actions={emptyStateActions}
            text={t('ContractTable.NoDataKey')}
          />
        ) : (
          <>
            <TableControlBar className={styles.controlBar}>
              <TableMassAction
                responsive
                actions={massActions}
                isAllSelected={isAllSelected}
                isLoading={isLoading}
                isRemoveSelection={isRemoveSelection}
                selectedSize={getSelectedSize({
                  exclude,
                  pagination,
                  selectAllPages,
                  selected,
                })}
                setMassAction={setMassAction}
                onSelectAll={handleSelectAll}
              />
              <PageActions isLoading={isLoading}>
                <TableControlButtons
                  DensityButton={null}
                  ResetSortingButton={getTableResetSortingButton(ordering)}
                  configButtonProps={{ onClick: handleConfigOpen }}
                  refreshButtonProps={{
                    isLoading: isRefetching,
                    onClick: handleRefresh,
                  }}
                  resetSortingProps={{ onClick: handleResetOrdering }}
                />
                <FilterDivider />
                <Pagination
                  responsive
                  value={pagination}
                  onChange={handleSetPagination}
                  onChangeSize={handleSetSize}
                />
              </PageActions>
            </TableControlBar>
            <Table
              TableRow={EntityTabContractTableRow}
              checkIsItemSelected={checkIsItemSelected}
              columns={columns}
              containerRef={pageScrollWrapperRef}
              context={TableContext.User}
              headerRowProps={{
                ordering,
                onApply: handleApplyConfig,
                onSetColumns: handleSetColumns,
                onSetOrdering: handleSetOrdering,
              }}
              isLoading={isLoading}
              items={items?.items}
              qaName={qaName.table}
              rowProps={{
                getActions: getRowActions,
                getOnClickProps: getContractsRowClickProps,
                hideCheckbox: massActions.length === 0,
                statuses: INHABITANT_STATUS,
                onCallAction: handleCallRowAction,
                onClick: handleRowClick,
                onMouseDown: handleRowMouseDown,
                onSelect: selectAllPages ? handleExclude : handleSelect,
              }}
            />
          </>
        )}
        {selected.length > 0 || selectAllPages ? (
          <SelectionToolbar
            allSelected={selectAllPages || selected.length === pagination.count}
            count={pagination.count}
            excludeCount={exclude.length}
            selected={selected.length}
            onClose={handleUnselectAllPages}
            onSelectAll={handleSelectAllPages}
          />
        ) : null}
        <TablePagination
          responsive
          isSkeleton={isLoading}
          value={pagination}
          onChange={handleSetPagination}
        />
        {isConfigOpen ? (
          <TableConfigModal
            columns={columns}
            entity={entity}
            requiredColumns={requiredColumns}
            setColumns={handleSetColumns}
            setConfigOpen={setConfigOpen}
            onApply={handleApplyConfig}
          />
        ) : null}
      </div>
    </ScreenContainerRef>
  )
}

export default EntityTabContracts
